/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

(() => {
  const emitDialogState = (open) => {
    document.dispatchEvent(new CustomEvent(open ? 'theme:dialog-opened' : 'theme:dialog-closed'));
  };

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.header = this.querySelector('[data-site-header]');
      this.navigation = this.querySelector('.desktop-navigation');
      this.megaTriggers = [...this.querySelectorAll('[data-mega-menu-trigger]')];
      this.dataset.menuEnhanced = '';
      this.desktopMedia = window.matchMedia('(min-width: 1025px)');
      this.motionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.configureAccountAccess();
      this.resizeObserver = new ResizeObserver(() => this.measureAnnouncement());
      this.refreshAnnouncement();
      this.updateScrollState();

      window.addEventListener('scroll', () => this.requestScrollUpdate(), { passive: true, signal });
      window.addEventListener('resize', () => this.requestScrollUpdate(), { passive: true, signal });
      document.addEventListener('shopify:section:load', () => this.refreshAnnouncement(), { signal });

      this.megaTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          this.openMegaMenu(trigger);
          this.hoverOpenedTrigger = trigger;
        }, { signal });
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          const expanded = trigger.getAttribute('aria-expanded') === 'true';
          if (expanded && (event.detail === 0 || this.hoverOpenedTrigger !== trigger)) this.closeMegaMenu(trigger);
          else this.openMegaMenu(trigger);
          this.hoverOpenedTrigger = null;
        }, { signal });
        trigger.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          this.openMegaMenu(trigger);
          this.querySelector(`#${trigger.getAttribute('aria-controls')}`)?.querySelector('a')?.focus();
        }, { signal });
      });

      this.querySelectorAll('[data-mega-menu]').forEach((menu) => {
        const firstGroup = menu.querySelector('[data-mega-group-trigger]');
        if (firstGroup) this.activateMegaGroup(firstGroup);
      });

      this.querySelectorAll('.desktop-navigation__link').forEach((link) => {
        link.addEventListener('focus', () => {
          this.megaTriggers.forEach((trigger) => {
            if (trigger !== link) this.closeMegaMenu(trigger);
          });
        }, { signal });
        if (!link.matches('[data-mega-menu-trigger]')) {
          link.addEventListener('pointerenter', () => this.closeAllMegaMenus(), { signal });
        }
      });

      this.querySelectorAll('[data-mega-group-trigger]').forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => this.activateMegaGroup(trigger), { signal });
        trigger.addEventListener('focus', () => this.activateMegaGroup(trigger), { signal });
        trigger.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.querySelector(`#${trigger.getAttribute('aria-controls')}`)?.querySelector('a')?.focus();
          }
        }, { signal });
      });

      this.header?.addEventListener('mouseleave', () => {
        if (!this.navigation?.querySelector(':focus-visible')) this.closeAllMegaMenus();
      }, { signal });
      this.navigation?.addEventListener('focusout', (event) => {
        if (!event.relatedTarget || !this.navigation.contains(event.relatedTarget)) this.closeAllMegaMenus();
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const openTrigger = this.megaTriggers.find((trigger) => trigger.getAttribute('aria-expanded') === 'true');
        if (!openTrigger) return;
        event.preventDefault();
        this.closeMegaMenu(openTrigger);
        openTrigger.focus();
      }, { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.navigation?.contains(event.target)) this.closeAllMegaMenus();
      }, { signal });
      this.desktopMedia.addEventListener('change', () => this.closeAllMegaMenus(), { signal });
      this.motionMedia.addEventListener('change', () => this.syncMenuVideos(), { signal });
      document.addEventListener('visibilitychange', () => this.syncMenuVideos(), { signal });
      document.addEventListener('shopify:block:select', (event) => {
        const card = event.target.closest('.mega-menu-card');
        if (!card || !this.contains(card)) return;
        const menu = card.closest('[data-mega-menu]');
        const trigger = this.megaTriggers.find((item) => item.getAttribute('aria-controls') === menu.id);
        if (trigger) this.openMegaMenu(trigger);
        const panel = card.closest('[data-mega-group-panel]');
        if (panel) this.activateMegaGroup(menu.querySelector(`[data-mega-group-trigger="${panel.dataset.megaGroupPanel}"]`));
        card.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }, { signal });
    }

    configureAccountAccess() {
      const isLocalPreview = ['127.0.0.1', 'localhost', '[::1]'].includes(window.location.hostname);
      if (!isLocalPreview) return;

      const accountComponent = this.querySelector('[data-account-component]');
      const localAccountLink = this.querySelector('[data-local-account-link]');
      if (accountComponent && localAccountLink) {
        accountComponent.hidden = true;
        localAccountLink.hidden = false;
      }

      this.querySelectorAll('[data-local-account-url]').forEach((link) => {
        link.href = link.dataset.localAccountUrl;
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      window.cancelAnimationFrame(this.scrollFrame);
      this.querySelectorAll('[data-menu-video]').forEach((video) => video.pause());
    }

    measureAnnouncement() {
      const height = this.announcements.reduce((total, bar) => total + bar.getBoundingClientRect().height, 0);
      document.documentElement.style.setProperty('--announcement-height', `${height}px`);
      this.updateScrollState();
    }

    refreshAnnouncement() {
      this.resizeObserver?.disconnect();
      this.announcements = [...document.querySelectorAll('[data-announcement-bar]')]
        .filter((bar) => !bar.closest('site-header'));
      this.announcements.forEach((bar) => this.resizeObserver?.observe(bar));
      if (this.header) this.resizeObserver?.observe(this.header);
      this.measureAnnouncement();
    }

    requestScrollUpdate() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateScrollState();
      });
    }

    updateScrollState() {
      if (!this.header) return;
      const { top, bottom } = this.header.getBoundingClientRect();
      // Fixed overlays follow the real header position while announcements scroll away.
      this.style.setProperty('--header-top', `${Math.max(0, top)}px`);
      this.style.setProperty('--header-bottom', `${Math.max(0, bottom)}px`);
      this.classList.toggle('is-scrolled', window.scrollY > 0 && top <= 0);
    }

    openMegaMenu(trigger) {
      if (!this.desktopMedia.matches) return;
      this.megaTriggers.forEach((candidate) => {
        if (candidate !== trigger) this.closeMegaMenu(candidate);
      });
      const menu = this.querySelector(`#${trigger.getAttribute('aria-controls')}`);
      if (!menu) return;
      trigger.setAttribute('aria-expanded', 'true');
      trigger.closest('details').open = true;
      menu.classList.add('is-open');
      this.classList.add('has-open-menu');
      this.syncMenuVideos();
    }

    closeMegaMenu(trigger) {
      if (this.hoverOpenedTrigger === trigger) this.hoverOpenedTrigger = null;
      const menu = this.querySelector(`#${trigger.getAttribute('aria-controls')}`);
      trigger.setAttribute('aria-expanded', 'false');
      trigger.closest('details').open = false;
      menu?.classList.remove('is-open');
      if (!this.megaTriggers.some((candidate) => candidate.getAttribute('aria-expanded') === 'true')) {
        this.classList.remove('has-open-menu');
      }
      this.syncMenuVideos();
    }

    closeAllMegaMenus() {
      this.megaTriggers.forEach((trigger) => this.closeMegaMenu(trigger));
    }

    activateMegaGroup(trigger) {
      const menu = trigger.closest('[data-mega-menu]');
      if (!menu) return;
      const group = trigger.dataset.megaGroupTrigger;
      menu.querySelectorAll('[data-mega-group-trigger]').forEach((candidate) => {
        candidate.classList.toggle('is-active', candidate === trigger);
        candidate.setAttribute('aria-expanded', String(candidate === trigger));
      });
      menu.querySelectorAll('[data-mega-group-panel]').forEach((panel) => {
        const active = panel.dataset.megaGroupPanel === group;
        panel.classList.toggle('is-active', active);
        panel.inert = !active;
        panel.setAttribute('aria-hidden', String(!active));
      });
      this.syncMenuVideos();
    }

    syncMenuVideos() {
      this.querySelectorAll('[data-menu-video]').forEach((video) => {
        const panel = video.closest('[data-mega-group-panel]');
        const visible = video.closest('[data-menu-disclosure]')?.open && (!panel || panel.classList.contains('is-active')) && !document.hidden;
        if (!visible) return video.pause();
        const sources = [...video.querySelectorAll('source[data-src]')];
        sources.forEach((source) => { source.src = source.dataset.src; source.removeAttribute('data-src'); });
        if (sources.length) {
          video.preload = 'metadata';
          video.load();
        }
        // Keep a still frame for reduced motion. No interactive controls inside a link.
        if (this.motionMedia.matches) video.pause();
        else video.play()?.catch(() => {});
      });
    }
  }

  class MobileMenu extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.root = this.closest('site-header');
      this.dialog = this.querySelector('[data-mobile-menu-dialog]');
      this.openButtons = [...(this.root?.querySelectorAll('[data-mobile-menu-open]') || [])];
      this.closeButton = this.querySelector('[data-mobile-menu-close]');
      this.mobileAnnouncement = this.querySelector('[data-mobile-announcement]');
      this.panelStack = [];
      this.resetPanels();
      this.desktopMedia = window.matchMedia('(min-width: 1025px)');
      this.desktopMedia.addEventListener('change', () => {
        if (this.desktopMedia.matches) this.close({ restoreFocus: false, immediate: true });
      }, { signal });

      this.openButtons.forEach((button) => button.addEventListener('click', () => this.open(button), { signal }));
      this.closeButton?.addEventListener('click', () => this.close(), { signal });
      this.querySelectorAll('[data-mobile-panel-open]').forEach((button) => {
        button.addEventListener('click', () => this.openPanel(button), { signal });
      });
      this.querySelectorAll('[data-mobile-panel-back]').forEach((button) => {
        button.addEventListener('click', () => this.closePanel(), { signal });
      });
      this.querySelectorAll('[data-mobile-menu-link]').forEach((link) => {
        link.addEventListener('click', () => this.close({ restoreFocus: false, immediate: true }), { signal });
      });
      this.querySelectorAll('[data-search-open]').forEach((button) => {
        button.addEventListener('click', () => this.close({ restoreFocus: false, immediate: true }), { signal });
      });
      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.panelStack.length ? this.closePanel() : this.close();
      }, { signal });
      this.dialog?.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        // Handle nested navigation before the browser's dialog close request.
        event.preventDefault();
        event.stopPropagation();
        this.panelStack.length ? this.closePanel() : this.close();
      }, { signal });
      this.dialog?.addEventListener('close', () => this.finishClose(), { signal });
    }

    disconnectedCallback() {
      this.close({ restoreFocus: false, immediate: true });
      this.controller?.abort();
      window.clearTimeout(this.closeTimer);
    }

    open(trigger) {
      if (!this.dialog || this.dialog.open) return;
      this.trigger = trigger;
      this.syncAnnouncement();
      this.dialog.showModal();
      trigger.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => this.dialog.classList.add('is-open'));
      this.hasScrollLock = true;
      emitDialogState(true);
      this.closeButton?.focus({ preventScroll: true });
    }

    syncAnnouncement() {
      if (!this.mobileAnnouncement) return;
      const source = document.querySelector('[data-announcement-bar]');
      this.mobileAnnouncement.replaceChildren();
      if (!source) return;
      const clone = source.cloneNode(true);
      clone.removeAttribute('data-announcement-bar');
      clone.removeAttribute('role');
      clone.removeAttribute('aria-label');
      this.mobileAnnouncement.append(clone);
    }

    close({ restoreFocus = true, immediate = false } = {}) {
      if (!this.dialog?.open) return;
      window.clearTimeout(this.closeTimer);
      this.dialog.classList.remove('is-open');
      this.openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      const finish = () => {
        if (this.dialog?.open) this.dialog.close();
        this.finishClose();
        if (restoreFocus) this.trigger?.focus({ preventScroll: true });
      };
      if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
      else this.closeTimer = window.setTimeout(finish, 500);
    }

    finishClose() {
      this.dialog?.classList.remove('is-open');
      this.openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.resetPanels();
      if (this.hasScrollLock) emitDialogState(false);
      this.hasScrollLock = false;
    }

    openPanel(trigger) {
      const panel = this.querySelector(`#${trigger.dataset.mobilePanelOpen}`);
      if (!panel) return;
      const previous = this.panelStack.at(-1)?.panel;
      if (previous) {
        previous.inert = true;
        previous.setAttribute('aria-hidden', 'true');
      }
      else {
        const menuRoot = this.querySelector('[data-mobile-menu-root]');
        if (menuRoot) {
          menuRoot.inert = true;
          menuRoot.setAttribute('aria-hidden', 'true');
        }
      }
      panel.classList.add('is-active');
      panel.setAttribute('aria-hidden', 'false');
      panel.inert = false;
      trigger.setAttribute('aria-expanded', 'true');
      this.panelStack.push({ panel, trigger });
      panel.querySelector('[data-mobile-panel-back]')?.focus({ preventScroll: true });
    }

    closePanel() {
      const current = this.panelStack.pop();
      if (!current) return;
      current.panel.classList.remove('is-active');
      current.panel.setAttribute('aria-hidden', 'true');
      current.panel.inert = true;
      current.trigger.setAttribute('aria-expanded', 'false');
      const previous = this.panelStack.at(-1)?.panel;
      if (previous) {
        previous.inert = false;
        previous.setAttribute('aria-hidden', 'false');
      }
      else {
        const menuRoot = this.querySelector('[data-mobile-menu-root]');
        if (menuRoot) {
          menuRoot.inert = false;
          menuRoot.setAttribute('aria-hidden', 'false');
        }
      }
      current.trigger.focus({ preventScroll: true });
    }

    resetPanels() {
      this.panelStack = [];
      this.querySelectorAll('[data-mobile-panel-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.querySelectorAll('[data-mobile-panel]').forEach((panel) => {
        panel.classList.remove('is-active');
        panel.setAttribute('aria-hidden', 'true');
        panel.inert = true;
      });
      const menuRoot = this.querySelector('[data-mobile-menu-root]');
      if (menuRoot) {
        menuRoot.inert = false;
        menuRoot.setAttribute('aria-hidden', 'false');
      }
    }
  }

  class HeaderCart extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.details = this.querySelector('details');

      document.addEventListener('pointerdown', (event) => {
        if (this.details?.open && !this.contains(event.target)) this.details.removeAttribute('open');
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.details?.open) {
          this.details.removeAttribute('open');
          this.querySelector('summary')?.focus();
        }
      }, { signal });
      this.addEventListener('click', (event) => this.handleCartControl(event), { signal });
      this.addEventListener('change', (event) => this.handleQuantityInput(event), { signal });
      document.addEventListener('theme:cart-updated', (event) => this.handleCartUpdate(event.detail || {}), { signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    handleCartControl(event) {
      const control = event.target.closest('[data-cart-quantity], [data-cart-remove]');
      if (!control || !this.contains(control)) return;
      event.preventDefault();
      const line = Number.parseInt(control.dataset.line, 10);
      const quantity = control.hasAttribute('data-cart-remove')
        ? 0
        : Number.parseInt(control.dataset.quantity, 10);
      this.changeLine(line, quantity);
    }

    handleQuantityInput(event) {
      const input = event.target.closest('[data-cart-quantity-input]');
      if (!input || !this.contains(input)) return;
      const line = Number.parseInt(input.dataset.line, 10);
      const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
      this.changeLine(line, quantity);
    }

    async changeLine(line, quantity) {
      if (this.cartRequestPending || !Number.isInteger(line) || line < 1 || !Number.isInteger(quantity) || quantity < 0) return;
      this.cartRequestPending = true;
      const popover = this.querySelector('.header-cart__popover');
      popover?.setAttribute('aria-busy', 'true');

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ line, quantity })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Cart update failed');
        document.dispatchEvent(new CustomEvent('theme:cart-updated', { detail: { cart: result } }));
      } catch (error) {
        console.error(error);
        document.dispatchEvent(new CustomEvent('theme:toast', {
          detail: { type: 'error', message: error.message || 'This item could not be updated. Please try again.' }
        }));
        popover?.removeAttribute('aria-busy');
        this.cartRequestPending = false;
      }
    }

    async handleCartUpdate({ cart, sections } = {}) {
      if (cart && Number.isFinite(cart.item_count)) {
        document.querySelectorAll('[data-cart-count]').forEach((count) => {
          count.textContent = cart.item_count;
          count.classList.toggle('is-empty', cart.item_count === 0);
        });
      }

      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;
      const wasOpen = Boolean(this.details?.open);
      try {
        let html = sections?.[sectionId];
        if (!html) {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}?sections=${encodeURIComponent(sectionId)}`, {
            headers: { Accept: 'application/json' }
          });
          if (!response.ok) throw new Error(`Header cart refresh failed: ${response.status}`);
          const renderedSections = await response.json();
          html = renderedSections[sectionId];
        }
        if (!html) return;

        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const replacement = documentFragment.querySelector(`[data-header-cart][data-section-id="${sectionId}"]`);
        if (!replacement) return;
        this.replaceWith(replacement);
        if (wasOpen) {
          replacement.querySelector('details')?.setAttribute('open', '');
          replacement.querySelector('summary')?.focus({ preventScroll: true });
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);
  if (!customElements.get('mobile-menu')) customElements.define('mobile-menu', MobileMenu);
  if (!customElements.get('header-cart')) customElements.define('header-cart', HeaderCart);
})();
